
import { defineComponent, onMounted, ref, watch, PropType, computed } from 'vue';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

// This type should align with how the data will be provided from the parent/API.
export interface PerformanceChartData {
  labels: string[]; // e.g., ['Jan', 'Feb', 'Mar']
  datasets: {
    label: string; // e.g., 'Successful Runs' or 'Failed Runs'
    data: number[];
    backgroundColor?: string;
    borderColor?: string;
    tension?: number;
  }[];
}

export default defineComponent({
  name: 'PerformanceChart',
  props: {
    data: {
      type: Object as PropType<PerformanceChartData>,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const chartCanvas = ref<HTMLCanvasElement | null>(null);
    let chartInstance: Chart | null = null;

    const hasData = computed(() => {
      return props.data && props.data.labels && props.data.labels.length > 0;
    });

    const renderChart = () => {
      if (!chartCanvas.value) return;
      
      // Validate data before rendering
      if (!props.data || !props.data.labels || props.data.labels.length === 0) {
        console.warn('PerformanceChart: No valid data to render');
        return;
      }
      
      console.log('PerformanceChart: Rendering with data:', props.data);
      
      if (chartInstance) {
        chartInstance.destroy();
      }

      // In Vue 2, we can use the data directly
      chartInstance = new Chart(chartCanvas.value, {
        type: 'line', // As per design, line/bar chart. Line is good for trends.
        data: {
          labels: [...props.data.labels], // Create a copy to avoid reactivity issues
          datasets: props.data.datasets.map(dataset => ({
            ...dataset,
            data: [...dataset.data], // Create a copy of the data array
          })),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
            },
            tooltip: {
              enabled: true,
              mode: 'index',
              intersect: false,
            },
          },
          scales: {
            x: {
              display: true,
              title: {
                display: true,
                text: 'Date',
              },
            },
            y: {
              display: true,
              title: {
                display: true,
                text: 'Number of Runs',
              },
              beginAtZero: true,
            },
          },
        },
      });
    };

    onMounted(() => {
      renderChart();
    });

    watch(() => props.data, (newData) => {
      console.log('PerformanceChart: Data changed, new data:', newData);
      // Use Vue.nextTick or setTimeout for Vue 2
      setTimeout(() => {
        renderChart();
      }, 0);
    }, { deep: true });

    return {
      chartCanvas,
      hasData,
    };
  },
});
